import React, { useEffect, useRef } from "react";
import Skeleton from "react-loading-skeleton";
import { AuditLog } from "../types";
import { useGetGroupUserInfoQuery } from "../../../../services/userAPI";

interface LogDetailsPanelProps {
  log: AuditLog | null;
  groupId: string;
  onClose: () => void;
}

const LogDetailsPanel: React.FC<LogDetailsPanelProps> = ({ log, groupId, onClose }) => {
  const {
    data: userInfo,
    isLoading: userLoading,
    error: userError,
  } = useGetGroupUserInfoQuery(
    {
      groupID: groupId,
      userID: log?.user_id || "",
    },
    {
      skip: !log?.user_id,
    }
  );

  const pannelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handlePannelClose = (event: MouseEvent) => {
      if (pannelRef && !pannelRef?.current?.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handlePannelClose);
    return () => {
      document.removeEventListener("mousedown", handlePannelClose);
    };
  }, [onClose, pannelRef]);

  if (!log) return null;

  return (
    <div
      ref={pannelRef}
      className="absolute inset-y-0 right-0 w-96 bg-white shadow-xl border-l border-gray-200 overflow-y-auto z-50"
    >
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          {/* <h2 className="text-lg font-semibold text-gray-900">Log Details</h2> */}
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="space-y-6">
          {/* User Information */}
          <div>
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 h-12 w-12">
                <div className="h-12 w-12 rounded-full bg-gray-300 flex items-center justify-center">
                  <span className="text-lg font-medium text-gray-700">
                    {userLoading ? (
                      <Skeleton height={20} width={20} circle />
                    ) : (
                      userInfo?.username?.charAt(0)?.toUpperCase() || "U"
                    )}
                  </span>
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-900">
                  {userLoading ? (
                    <Skeleton height={16} width={120} />
                  ) : (
                    userInfo?.username || "Unknown User"
                  )}
                </div>
                <div className="text-sm text-gray-500">
                  {userLoading ? (
                    <Skeleton height={16} width={150} />
                  ) : (
                    userInfo?.email || "Unknown Email"
                  )}
                </div>
                <div className="text-xs text-gray-400">
                  {userLoading ? (
                    <Skeleton height={14} width={60} />
                  ) : (
                    userInfo?.groups?.[groupId]?.group_name || "Member"
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Action Details */}
          <div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Action:</span>
                <span className="text-sm font-medium text-gray-900">
                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                    {log.action}
                  </span>
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Resource Type:</span>
                <span className="text-sm font-medium text-gray-900">
                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                    {log.resource_type}
                  </span>
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Resource ID:</span>
                <span className="text-sm font-medium text-gray-900 font-mono">
                  {log.resource_id}
                </span>
              </div>
            </div>
          </div>

          {/* Timestamp */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-3">Timestamp</h3>
            <div className="text-sm text-gray-900">
              {new Date(log.timestamp).toLocaleString()}
            </div>
          </div>

          {/* Details */}
          {log.details && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-3">
                Additional Details
              </h3>
              <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                {log.details}
              </div>
            </div>
          )}

          {/* IP Address */}
          {log.ip_address && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-3">IP Address</h3>
              <div className="text-sm text-gray-900 font-mono">{log.ip_address}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LogDetailsPanel;
